import * as React from "react";
import DataHeader from "./molecules/DataHeader";
import { DataGrid } from "./organisms/DataGrid/index";
import { ExtractedDataField } from "../../../types/dataTypes";
import { useSchemaExtractionData } from "../hooks/useSchemaExtractionData";
import { Spinner } from "@/components/ui/spinner";
import { AlertTriangle } from "lucide-react";

interface DataPanelProps {
  onMapToWord?: (field: ExtractedDataField) => void;
  executionId?: string;
  userId?: string;
}

const DataPanel: React.FC<DataPanelProps> = ({ onMapToWord, executionId, userId }) => {
  const { schemas, isLoading, error, validateField } = useSchemaExtractionData(executionId, userId);

  const handleViewField = (field: ExtractedDataField) => {
    console.log('View field in document:', field);
    // TODO: Implement field viewing functionality
    // This could open a modal or navigate to a document viewer
  };

  const handleMapToWord = (field: ExtractedDataField) => {
    if (onMapToWord) {
      onMapToWord(field);
    } else {
      // Mock Word integration
      console.log('Mapping field to Word:', field);
      alert(`Mapping "${field.name}: ${field.value}" to Word document`);
    }
  };

  const handleValidateField = async (schemaId: string, fieldId: string, isValid: boolean | null) => {
    try {
      await validateField(schemaId, fieldId, isValid);
      console.log(`Field ${fieldId} validation updated to ${isValid}`);
    } catch (error) {
      console.error('Error validating field:', error);
      alert('Error updating field validation');
    }
  };

  const handleFieldUpdate = async (fieldId: string, newValue: string) => {
    try {
      // TODO: Implement field update API call
      console.log(`Updating field ${fieldId} with new value:`, newValue);
      
      // For now, just show a success message
      // In a real implementation, this would call an API to update the field
      alert(`Field updated: "${newValue}"`);
      
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Error updating field');
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto p-4 md:p-6 bg-slate-50">
        <DataHeader 
          title="Data Management"
          subtitle="Loading extraction schemas..."
          className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between md:mb-6"
        />
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="mt-4 text-slate-500">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 overflow-auto p-4 md:p-6 bg-slate-50">
        <DataHeader 
          title="Data Management"
          subtitle="Error loading data"
          className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between md:mb-6"
        />
        <div className="flex flex-col items-center justify-center py-12 text-red-500">
          <AlertTriangle className="text-4xl mb-4" />
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6 bg-slate-50">
      <DataHeader 
        title="Data Management"
        subtitle="Extraction schemas and data extracted from the current execution"
        className="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between md:mb-6"
      />
      
      <DataGrid
        schemas={schemas}
        onViewField={handleViewField}
        onMapToWord={handleMapToWord}
        onValidateField={handleValidateField}
        onFieldUpdate={handleFieldUpdate}
        className="flex flex-col gap-3 mb-4 md:gap-4 md:mb-6"
      />
    </div>
  );
};

export default DataPanel;
